import { useCallback, useMemo } from 'react'
import { authService } from '@/services/authService'

/**
 * Hook simplificado para control de acceso basado en roles del backend
 * El backend de Pescetti usa un sistema simple de roles y niveles:
 * - USER: nivel 0 (usuario regular)
 * - ADMIN: nivel 1
 * - OWNER: nivel 2
 * - DEV: nivel 3
 */
export function useAccess() {
  const user = authService.getUser()
  const hasUser = useMemo(() => !!user, [user?.id])

  // Normalizar el rol: REGULAR del backend se mapea a USER en el frontend
  const role = useMemo(() => {
    const backendRole = user?.role || 'USER'
    // Mapear REGULAR a USER para compatibilidad
    return backendRole === 'REGULAR' ? 'USER' : backendRole
  }, [user?.role])

  const isAdmin = useMemo(() => {
    return role === 'ADMIN' || role === 'DEV' || role === 'OWNER'
  }, [role])

  const isOwner = useMemo(() => {
    return role === 'OWNER' || role === 'DEV'
  }, [role])

  const isDeveloper = useMemo(() => {
    return role === 'DEV'
  }, [role])

  /**
   * Verifica si el usuario tiene acceso a una ruta según su nivel
   * @param requiredLevel - Nivel requerido (0: USER, 1: ADMIN, 2: OWNER, 3: DEV)
   */
  const hasLevel = useCallback((requiredLevel: number): boolean => {
    const levelMap: Record<string, number> = {
      USER: 0,
      REGULAR: 0, // REGULAR es equivalente a USER
      ADMIN: 1,
      OWNER: 2,
      DEV: 3,
    }
    const userLevel = levelMap[role] ?? 0
    return userLevel >= requiredLevel
  }, [role])

  /**
   * Verifica acceso a rutas según las restricciones de roles
   */
  const canAccess = useCallback((path: string): boolean => {
    // Usuarios no autenticados no tienen acceso
    if (!hasUser) return false
    
    // Permitir siempre dashboard y login
    if (path === '/dashboard' || path === '/login' || path === '/') return true
    
    // Desarrollador y Propietario: acceso completo
    if (role === 'DEV' || role === 'OWNER') {
      return true
    }
    
    // Administrador: puede ver todo MENOS Recursos, Productos Profesionales, Estadísticas
    // Puede acceder a Configuración pero solo ver "Acerca del Sistema"
    if (role === 'ADMIN') {
      const restrictedPaths = ['/recursos', '/productos-profesionales', '/estadisticas']
      return !restrictedPaths.includes(path)
    }
    
    // Usuario (Observador): solo acceso a Inicio y Estadísticas
    // Manejar tanto USER como REGULAR (del backend)
    if (role === 'USER' || role === 'REGULAR') {
      const allowedPaths = ['/estadisticas']
      return allowedPaths.includes(path)
    }
    
    return false
  }, [hasUser, role])
  
  /**
   * Verifica si el usuario puede escribir/editar (no solo leer)
   */
  const canWrite = useCallback((): boolean => {
    if (!hasUser) return false
    // Desarrollador y Propietario: pueden escribir
    if (role === 'DEV' || role === 'OWNER') return true
    // Administrador: puede escribir excepto en Productos (solo lectura)
    if (role === 'ADMIN') return true
    // Usuario: solo lectura en todo
    return false
  }, [hasUser, role])
  
  /**
   * Verifica si el usuario puede editar productos específicamente
   */
  const canEditProducts = useCallback((): boolean => {
    if (!hasUser) return false
    // Desarrollador y Propietario: pueden editar productos
    if (role === 'DEV' || role === 'OWNER') return true
    // Administrador y Usuario: solo lectura en productos
    return false
  }, [hasUser, role])

  return {
    loading: false,
    isAdmin,
    isOwner,
    isDeveloper,
    role,
    canAccess,
    canWrite,
    canEditProducts,
    hasLevel,
    user,
  }
}


